import { HexColorPicker } from "react-colorful";
import { useState, useRef, useEffect, CSSProperties } from "react";
import { createPortal } from "react-dom";

import "./SwatchPicker.css";

interface Props {
    color: string;
    title?: string;
    style?: CSSProperties;
    onChange?: (val: string) => void;
    disabled?: boolean;
    readOnly?: boolean;
}
export default function SwatchPicker({
    color,
    style,
    title,
    readOnly,
    disabled,
    onChange,
}: Props) {
    // Controls the visibility of the HexColorPicker
    const [showPicker, setShowPicker] = useState(false);

    // This is used to control the style of the red slash
    // the visually marks the control as disabled.
    const [slashStyle, setSlashStyle] = useState({});

    // Requisite references.
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const pickerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // If the button is disabled
        if (disabled) {
            // Get the button or return if unable
            const button = buttonRef.current;
            if (!button) return;

            // Get the size of the button
            const width = button.offsetWidth;
            const height = button.offsetHeight;

            // Using Pythagorean theorem, get the diagonal side length
            const diag = Math.sqrt(width ** 2 + height ** 2);

            // Using atan2, calculate the angle of the slash.
            const angle = Math.atan2(height, width) * (180 / Math.PI);

            // Set the style info.
            setSlashStyle({
                "--slash-len": `${diag}px`,
                "--slash-deg": `-${angle}deg`,
            });
            return; // Prevent the code below as clicking is disabled
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(event.target as Node)
            ) {
                setShowPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [disabled]);

    return (
        <div
            className={`swatch-picker ${disabled ? "disabled" : ""}`}
            style={{ ...style, ...slashStyle }}
            title={title ? title : "Color Picker"}
        >
            <button
                className="color-swatch"
                style={{
                    backgroundColor: color,
                    position: "relative",
                    overflow: "hidden",
                    ...style,
                }}
                onClick={() => {
                    if (readOnly) return;
                    setShowPicker((prev) => !prev);
                }}
                disabled={disabled}
                ref={buttonRef}
            />

            {showPicker &&
                createPortal(
                    <div
                        className="picker-popover"
                        ref={pickerRef}
                        style={{
                            top:
                                (buttonRef.current?.getBoundingClientRect()
                                    .bottom ?? 0) +
                                window.scrollY +
                                5,
                            left:
                                buttonRef.current?.getBoundingClientRect()
                                    .left ?? 0,
                            zIndex: 999,
                        }}
                    >
                        <HexColorPicker color={color} onChange={onChange} />
                        <input
                            className="hex-input"
                            type="text"
                            value={color}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (
                                    /^#?[0-9A-Fa-f]{0,6}$/.test(value) &&
                                    onChange
                                )
                                    onChange(`${value}`);
                            }}
                        />
                    </div>,
                    document.body,
                )}
        </div>
    );
}
